import {useEffect, useState, useMemo} from "react"
import {searchMessages} from "./searchMessages" // <— the function above

export function useSearchMessages(
  searchTerm?: string,
  dateRanges?: {start: string; end: string}[],
  participantProfileIds?: string[],
  page = 0,
  limit = 100
) {
  const [data, setData] = useState<
    {thread_uid: string; message_id: string}[] | null
  >(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [hasMore, setHasMore] = useState(false)

  // Memoize the search parameters to ensure stable reference
  const searchParams = useMemo(
    () => ({
      searchTerm,
      dateRanges,
      participantProfileIds,
      page,
      limit,
    }),
    [searchTerm, dateRanges, participantProfileIds, page, limit]
  )

  // Memoize the empty check to ensure stable reference
  const isEmpty = useMemo(
    () => !searchTerm && !dateRanges?.length && !participantProfileIds?.length,
    [searchTerm, dateRanges, participantProfileIds]
  )

  // Parameter debugging removed

  /**
   * Execute search when parameters change
   * Handles edge cases and error conditions
   */
  useEffect(() => {
    // Skip search if no filters or search term provided
    if (isEmpty) {
      setData([])
      setIsLoading(false)
      setError(null)
      setHasMore(false)
      return
    }

    // Validate participantProfileIds format
    if (participantProfileIds && !Array.isArray(participantProfileIds)) {
      setError(new Error("Invalid participants format"))
      setIsLoading(false)
      return
    }

    /**
     * Execute the search request and handle the response
     */
    const doSearch = async () => {
      setIsLoading(true)
      setError(null)
      try {
        // Call the search function with current parameters
        const result = await searchMessages(searchParams)

        if (result.error) {
          // Handle API error response
          setError(result.error)
        } else {
          // Process successful results
          setData(result.data)
          setHasMore(result.hasMore)
        }
      } catch (err) {
        // Handle unexpected errors
        setError(
          err instanceof Error ? err : new Error("An unknown error occurred")
        )
      } finally {
        setIsLoading(false)
      }
    }

    doSearch()
  }, [searchParams, isEmpty]) // Only depend on memoized values

  const result = useMemo(
    () => ({
      data,
      error,
      isLoading,
      hasMore,
    }),
    [data, error, isLoading, hasMore]
  )

  return result
}
